// @ts-nocheck
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchProductsFromApi,
    selectAllProducts,
    selectProductsStatus,
    selectProductsUsedFallback,
} from "../../stores/menu/productsSlice";
import { cartProducts } from "../../stores/cart/cartSlice";
import ProductDetailCard from "../../components/ProductDetailCard";
import { CategoryTabs } from "../../components/CategoryTabs";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguageContext } from "../../context/languageContext";
import { FaShoppingBag, FaArrowRight, FaSearch, FaWifi } from "react-icons/fa";
import useActiveSection from "../../hooks/useActiveSection";

const Menu = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const products = useSelector(selectAllProducts);
    const status = useSelector(selectProductsStatus);
    // The menu on screen is the bundled copy: the API is unreachable, so an order
    // could not be sent even if the guest built one.
    const offline = useSelector(selectProductsUsedFallback);
    const productsInCart = useSelector(cartProducts);
    const { t } = useLanguageContext();

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const categoryId = queryParams.get("categoryId");
    const itemId = queryParams.get("itemId");

    const [searchQuery, setSearchQuery] = useState("");
    const [highlightId, setHighlightId] = useState(null);

    const businessId = useSelector((state) => state.business.businessId) || '2';

    useEffect(() => {
        if (businessId) {
            dispatch(fetchProductsFromApi({ businessid: businessId }));
        }
    }, [dispatch, businessId]);

    const scrollToCategory = useCallback((id, behavior = "smooth") => {
        const el = document.querySelector(`[data-section-id="${id}"]`);
        if (el) el.scrollIntoView({ behavior, block: "start" });
    }, []);

    // Arriving from the home screen: land on the dish that was tapped, or on the
    // category when the whole category was asked for.
    useEffect(() => {
        if (products.length === 0) return undefined;
        if (!categoryId && !itemId) return undefined;

        const timer = window.setTimeout(() => {
            const dish = itemId && document.querySelector(`[data-item-id="${itemId}"]`);
            if (dish) {
                dish.scrollIntoView({ behavior: "auto", block: "center" });
                setHighlightId(itemId);
            } else if (categoryId) {
                scrollToCategory(categoryId, "auto");
            }
        }, 60);
        return () => window.clearTimeout(timer);
    }, [categoryId, itemId, products.length, scrollToCategory]);

    // Let the highlight fade once the guest has seen which dish they picked.
    useEffect(() => {
        if (!highlightId) return undefined;
        const timer = window.setTimeout(() => setHighlightId(null), 2200);
        return () => window.clearTimeout(timer);
    }, [highlightId]);

    const isLoading = status === "loading";

    // Guests search in whatever language they read the menu in, so match the
    // stored name and every translation of it.
    const matchesSearch = (item) => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return true;
        const haystack = [item.name, ...Object.values(item.itemTranslation || {})]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
        return haystack.includes(query);
    };

    const isSearching = searchQuery.trim().length > 0;
    // Tracks the category under the tab bar; idle while a search is showing.
    const activeId = useActiveSection(140, !isSearching && products.length > 0);
    // A search runs across the whole menu - a guest typing "kafe" should not have
    // to be standing on the right category to find it.
    const searchResults = isSearching
        ? products.flatMap((category) => category.menuItems || []).filter(matchesSearch)
        : [];

    const totalDishes = products.reduce((n, c) => n + (c.menuItems?.length || 0), 0);
    const totalItemsInCart = productsInCart.reduce((acc, item) => acc + (item.amount || 1), 0);

    return (
        <div className="flex-1 bg-white text-sharp">
            {/* Minimalist Sub-Header with Search */}
            <div className="px-5 pt-8 pb-4 space-y-4">
                <div className="flex items-end justify-between">
                    <div className="space-y-1">
                        <h1 className="text-2xl font-clash text-slate-900 tracking-tight leading-none">{t("Menu")}</h1>
                        <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">
                            {totalDishes} {t("dishes", "dishes")}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-100">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                        <span className="text-[10px] font-black uppercase text-slate-400">{t("Kitchen Open")}</span>
                    </div>
                </div>

                {/* Intelligent Search Bar */}
                <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-brand transition-colors">
                        <FaSearch size={14} />
                    </div>
                    <input
                        type="text"
                        placeholder={t("Search menu...", "Search menu...")}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-100 rounded-2xl pl-11 pr-4 py-3.5 text-sm font-medium placeholder:text-slate-300 focus:bg-white focus:border-brand/20 focus:ring-4 focus:ring-brand/5 outline-none transition-all"
                    />
                </div>

                {/* Says why there is nothing to tap: the dishes and prices are the saved
                    ones, and no order can leave the phone until the connection is back. */}
                {offline && (
                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-100/60">
                        <FaWifi className="text-amber-500 mt-0.5 flex-shrink-0" size={12} />
                        <p className="text-[11px] font-medium leading-snug text-amber-700/90">
                            {t("offlineMenuNotice", "Showing the saved menu - ordering is unavailable right now.")}
                        </p>
                    </div>
                )}
            </div>

            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin"></div>
                    <p className="mt-4 text-[10px] font-black uppercase tracking-widest text-slate-300">{t("Curating Menu")}</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {/* Tabs jump to a category rather than swapping the page; they step
                        aside while a search is running, since that spans everything. */}
                    {!isSearching && (
                        <CategoryTabs
                            categories={products}
                            activeId={activeId}
                            onSelect={(category) => scrollToCategory(category.id)}
                        />
                    )}

                    {isSearching ? (
                        <div className="px-5 pt-2 pb-32 space-y-4">
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                {searchResults.length} {t("dishes", "dishes")}
                            </p>
                            {searchResults.length > 0 ? (
                                <div className="grid grid-cols-2 gap-x-4 gap-y-6">
                                    {searchResults.map((product, index) => (
                                        <div key={product.id || index} className="animate-app-reveal">
                                            <ProductDetailCard product={product} />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="py-20 text-center space-y-3">
                                    <div className="mx-auto w-12 h-12 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-200">
                                        <FaSearch size={20} />
                                    </div>
                                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-300">{t("No matches found")}</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* One long page: every category, one after the other */
                        <div className="pb-32">
                            {products.map((category, index) => (
                                <section
                                    key={category.id || index}
                                    data-section-id={category.id}
                                    style={{ scrollMarginTop: "calc(var(--app-header-h, 64px) + var(--app-tabbar-h, 54px) + 12px)" }}
                                    className="px-5 pt-5"
                                >
                                    <div className="mb-4 space-y-1.5">
                                        <h2 className="text-lg font-clash text-slate-950 leading-none">
                                            {t(`categories.${category.categoryName}`, category.categoryName)}
                                        </h2>
                                        <div className="h-1 w-8 bg-brand rounded-full"></div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-x-4 gap-y-6">
                                        {(category.menuItems || []).map((product, i) => (
                                            <div
                                                key={product.id || i}
                                                data-item-id={product.id}
                                                className={`rounded-2xl transition-all duration-500 ${String(highlightId) === String(product.id)
                                                    ? "ring-2 ring-brand ring-offset-2 ring-offset-white"
                                                    : ""
                                                    }`}
                                            >
                                                <ProductDetailCard product={product} />
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Floating Quick-Checkout Bar - nothing to check out while offline */}
            {!offline && totalItemsInCart > 0 && (
                <div className="fixed bottom-24 inset-x-0 z-40 px-5 pointer-events-none animate-app-reveal">
                    <button
                        onClick={() => navigate("/cart")}
                        className="pointer-events-auto w-full h-16 flex items-center justify-between bg-slate-900 text-white rounded-[1.25rem] shadow-[0_20px_40px_-10px_rgba(0,0,0,0.3)] hover:scale-[1.02] active:scale-95 transition-all duration-300 overflow-hidden group"
                    >
                        <div className="flex items-center gap-4 pl-6">
                            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                                <FaShoppingBag className="text-white" size={16} />
                            </div>
                            <div className="text-left">
                                <p className="text-xs font-black uppercase tracking-widest text-white/40 leading-none mb-1">{t("View Cart")}</p>
                                <p className="text-base font-clash text-white tracking-tight leading-none">{totalItemsInCart} {t("Items Selected")}</p>
                            </div>
                        </div>
                        <div className="h-full px-6 flex items-center bg-brand transition-colors group-hover:bg-brand-light">
                            <FaArrowRight className="text-white" size={14} />
                        </div>
                    </button>
                </div>
            )}
        </div>
    );
};

export default Menu;
